<script setup>
    import { defineExpose, defineEmits, ref } from 'vue';
    import { useConsultationWebStore } from '@/stores/consultation/consultationWebStore';
    import { inject } from 'vue';
    import { getCurrentInstance } from 'vue';

    // Store
    import { useCommonStore } from '@/stores/common/commonStore';
    import { useLanguageStore } from '@/stores/common/languageStore';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    import utils from '@/commonProject/helpers/utils';
    import i18n from '@/common/i18n';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');
    // web相談ストア
    const consultationWebStore = useConsultationWebStore();
    // Web相談詳細情報レスポンス
    const webdtlResponce = ref({});
    // Web相談PK
    const webCngPk = ref('');
    // モーダルフラグ
    const detailArmDispFlg = ref('');
    const finishConfirmDispFlg = ref('');
    const finishCompleteDispFlg = ref('');

    const languageStore = useLanguageStore();

    const app = getCurrentInstance();

    const API_URL = app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S;

    const commonStore = useCommonStore();

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    /**
     * 初期処理 親画面から利用可能関数
     * @param WebCngPk Web相談PK
     */
    async function setInfo(WebCngPk) {
        // モーダル側で保持しているデータの初期化
        consultationWebStore.messageList = {};

        webdtlResponce.value = {};
        webCngPk.value = '';
        webCngPk.value = WebCngPk;
        // モーダルフラグ設定
        detailArmDispFlg.value = 'block';
        finishConfirmDispFlg.value = '';
        finishCompleteDispFlg.value = '';
        // Web相談詳細情報取得
        await getRegistrationInformation(WebCngPk);
        // Web相談メッセージ一覧取得
        await getMessageList(WebCngPk);
        // Web相談メッセージ既読登録
        await registerAlreadyRead(WebCngPk);

        // モーダルのHTML要素を取得する。
        const elm1 = document.querySelector('#modalWebConsultationDetailType1 .modalContents');

        // モーダルの縦スクロールを先頭に戻す。
        elm1.scrollTop = 0;
    }

    /**
     * 再表示
     */
    async function reDisplay() {
        await setInfo(webCngPk.value);
    }
    defineExpose({
        setInfo,
        reDisplay,
    });

    /**
     * Web相談詳細情報取得処理
     * @param WebCngPk Web相談PK
     */
    async function getRegistrationInformation(WebCngPk) {
        const queryParam = {
            Lang: languageStore.getLanguageValue,
            WebCngPk: WebCngPk,
        };
        const response = await consultationWebStore.getRegistrationInformation(API_URL, queryParam);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * Web相談メッセージ一覧取得処理
     * @param WebCngPk Web相談PK
     */
    async function getMessageList(WebCngPk) {
        const requestBody = {
            Lang: languageStore.getLanguageValue,
            WebCngPk: WebCngPk,
        };
        const response = await consultationWebStore.getMessageList(API_URL, requestBody);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * Web相談メッセージ既読登録処理
     * @param WebCngPk Web相談PK
     */
    async function registerAlreadyRead(WebCngPk) {
        if (consultationWebStore.messageList.WebCngMsgs.length !== 0) {
            for (let i = 0; i < consultationWebStore.messageList.WebCngMsgs.length; i++) {
                // メッセージタイプがカウンセラーで未読のメッセージを既読登録する。
                if (consultationWebStore.messageList.WebCngMsgs[i].WebCngMsgType === '2' && !consultationWebStore.messageList.WebCngMsgs[i].WebCngReaded) {
                    const requestBody = {
                        WebCngPk: WebCngPk,
                        WebCngMsgPk: consultationWebStore.messageList.WebCngMsgs[i].WebCngMsgPk,
                        WebCngDeleted: consultationWebStore.messageList.WebCngMsgs[i].WebCngDeleted,
                    };
                    const response = await consultationWebStore.registerAlreadyRead(API_URL, requestBody);
                    if (!response.success) {
                        messageDialog('error', response.errorMessage);
                        if (response.redirect) {
                            location.href = response.redirectUrl;
                        }
                        return;
                    }
                }
            }
        }
    }

    /**
     * Web相談メッセージ登録処理
     * @param WebCngMsgMode Web相談メッセージ種別("1"：正式、"2"：一時保存)
     */
    async function registerMessage(WebCngMsgMode) {
        // メッセージ入力欄が未入力の場合。
        if (utils.isNullEmpty(consultationWebStore.messageList.WebCngDraftMsg)) {
            messageDialog('error', i18n.global.t('consultationWebDetailArm.message.message1'));
            return;
        } else {
            // 文字数が超過している場合。
            if (utils.checkLengthOfLongString(consultationWebStore.messageList.WebCngDraftMsg)) {
                messageDialog('error', i18n.global.t('consultationWebDetailArm.message.message2'));
                return;
            }
        }
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        const requestBody = {
            WebCngPk: webCngPk.value,
            WebCngMsgMode: WebCngMsgMode,
            WebCngMsg: consultationWebStore.messageList.WebCngDraftMsg,
        };
        const response = await consultationWebStore.registerMessage(API_URL, requestBody);
        if (response.success) {
            reDisplay();
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * Web相談詳細モーダル「相談終了」
     */
    function nextModal() {
        detailArmDispFlg.value = '';
        finishConfirmDispFlg.value = 'block';
    }

    /**
     * Web相談終了確認モーダル「いいえ」
     */
    function backModal() {
        detailArmDispFlg.value = 'block';
        finishConfirmDispFlg.value = '';
    }

    /**
     * Web相談終了確認モーダル「はい」
     * @param WebCngOpeMode Web相談操作種別("1"：相談登録(正式)、"2"：相談登録(一時保存)、"3"：一時保存削除、"4"：相談終了)
     */
    async function register(WebCngOpeMode) {
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        const requestBody = {
            WebCngOpeMode: WebCngOpeMode,
            WebCngPk: webCngPk.value,
            NoticeHistPk: consultationWebStore.webdtlResponce.NoticeHistPk,
            WebCngArmSubj: consultationWebStore.webdtlResponce.WebCngArmSubj,
            WebCngArmIsuPk: consultationWebStore.webdtlResponce.WebCngArmIsuPk,
            WebCngArmCont: consultationWebStore.webdtlResponce.WebCngArmCont,
            WebCngTmsCat1Pk: consultationWebStore.webdtlResponce.WebCngTmsCat1Pk,
            WebCngTmsCat2Pk: consultationWebStore.webdtlResponce.WebCngTmsCat2Pk,
            WebCngTmsCat3Pk: consultationWebStore.webdtlResponce.WebCngTmsCat3Pk,
            WebCngTmsFormPk: consultationWebStore.webdtlResponce.WebCngTmsFormPk,
            WebCngTmsFormData: consultationWebStore.webdtlResponce.WebCngTmsFormData,
        };
        const response = await consultationWebStore.registerConsultationWeb(API_URL, requestBody);
        if (response.success) {
            finishConfirmDispFlg.value = '';
            finishCompleteDispFlg.value = 'block';
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    /**
     * 親画面で実装必須関数
     */
    const emit = defineEmits(['detailArmModal:cancel', 'detailArmModal:cancelUpdate', 'detailArmModal:regist', 'detailArmModal:OpenSuerveyApply']);
    function cancel() {
        // 親画面のキャンセル処理を呼び出す
        emit('detailArmModal:cancel');
    }
    function cancelUpdate() {
        // 親画面の更新キャンセル処理を呼び出す
        emit('detailArmModal:cancelUpdate');
    }
    function OpenSuerveyApply() {
        emit('detailArmModal:OpenSuerveyApply');
    }
</script>
<template>
    <article id="modalWebConsultationDetailType1" class="modal" :style="{ display: detailArmDispFlg }">
        <h3>{{ $t('consultationWebDetailArm.parts.modalTitle1') }}</h3>
        <section class="modalContents">
            <section class="form">
                <div class="dlGroup">
                    <dl id="counselingStartDate">
                        <!--相談開始日時-->
                        <dt>
                            <span>{{ $t('consultationWebDetailArm.parts.dl3') }}</span>
                        </dt>
                        <dd>
                            <span>{{ consultationWebStore.webdtlResponce.WebCngOpenDt }}</span>
                        </dd>
                    </dl>
                    <dl id="counselingEndDate">
                        <!--相談終了日時とステータス-->
                        <dt v-if="consultationWebStore.webdtlResponce.WebCngStatus === '3'">
                            <!--Web相談ステータスが「相談終了」の場合のみ表示する-->
                            <span>{{ $t('consultationWebDetailArm.parts.dl4') }}</span>
                        </dt>
                        <dd v-if="consultationWebStore.webdtlResponce.WebCngStatus === '3'">
                            <!--Web相談ステータスが「相談終了」の場合のみ表示する-->
                            <span>{{ consultationWebStore.webdtlResponce.WebCngCloseDt }}</span>
                        </dd>
                        <dd id="counselingStatus">
                            <template v-if="consultationWebStore.webdtlResponce.WebCngStatus === '2'">
                                <!--Web相談ステータスが「相談中」の場合-->
                                <!--「相談中」を表示する-->
                                <span id="complete">{{ $t('consultationWebDetailArm.parts.dl1') }}</span>
                            </template>
                            <template v-else-if="consultationWebStore.webdtlResponce.WebCngStatus === '3'">
                                <!--Web相談ステータスが「相談終了」の場合-->
                                <template v-if="consultationWebStore.webdtlResponce.QuestionStatus !== '2'">
                                    <!--アンケートステータスが「未回答」-->
                                    <!--「相談終了」を表示する-->
                                    <span id="complete">{{ $t('consultationWebDetailArm.parts.dl2') }}</span>
                                </template>
                                <template v-else>
                                    <!--アンケートステータスが「未回答」-->
                                    <!--「相談後アンケート回答済み」を表示する-->
                                    <span id="complete">{{ $t('consultationWebDetailArm.parts.dl2') }}</span>
                                    <span id="complete">{{ $t('consultationWebDetailArm.parts.dl5') }}</span>
                                </template>
                            </template>
                        </dd>
                    </dl>
                </div>
                <dl class="line">
                    <!--Web相談の詳細情報-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('consultationWebDetailArm.parts.dl6') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <span>{{ consultationWebStore.webdtlResponce.WebCngArmSubj }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl class="line">
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('consultationWebDetailArm.parts.dl7') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <span>{{ consultationWebStore.webdtlResponce.WebCngArmIsuNm }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('consultationWebDetailArm.parts.dl8') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <div class="message">{{ consultationWebStore.webdtlResponce.WebCngArmCont }}</div>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <hr class="line" />
                <dl class="line">
                    <!--返信-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('consultationWebDetailArm.parts.dl9') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="contactHistoryBlock">
                            <template v-for="(massage, index) in consultationWebStore.messageList.WebCngMsgs" :key="index">
                                <li :class="{ counselor: massage.WebCngMsgType === '2' }">
                                    <ul>
                                        <li>
                                            <span>
                                                {{ commonStore.getConvertDictionary('WebCngMsgType', String(massage.WebCngMsgType), languageStore.getLanguageValue) }}
                                            </span>
                                        </li>
                                        <li>
                                            <span>{{ massage.WebCngMsgSendDt }}</span>
                                        </li>
                                    </ul>
                                    <div class="message">
                                        <template v-if="massage.WebCngDeleted">
                                            <p>{{ $t('common.parts.messageDeleted') }}</p>
                                        </template>
                                        <template v-else>
                                            <p>{{ massage.WebCngMsg }}</p>
                                        </template>
                                    </div>
                                </li>
                            </template>
                        </ul>
                    </dd>
                </dl>
                <dl class="line" v-if="consultationWebStore.webdtlResponce.WebCngStatus !== '3'">
                    <!--新しい返信-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('consultationWebDetailArm.parts.dl10') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd id="newMessage">
                        <ul class="formInput">
                            <li>
                                <div id="textArea">
                                    <textarea style="height: 140px" v-model="consultationWebStore.messageList.WebCngDraftMsg" />
                                </div>
                            </li>
                        </ul>
                        <ul class="btnArea right">
                            <li>
                                <button type="button" class="btnSecondary" v-if="consultationWebStore.webdtlResponce.WebCngStatus !== '3'" @click="registerMessage('2')" :disabled="disabledStore.isDisabled">{{ $t('consultationWebDetailArm.parts.modalButton1') }}</button>
                            </li>
                            <li>
                                <button type="button" class="btnPrimary" v-if="consultationWebStore.webdtlResponce.WebCngStatus !== '3'" @click="registerMessage('1')" :disabled="disabledStore.isDisabled">{{ $t('consultationWebDetailArm.parts.modalButton2') }}</button>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <ul class="btnArea center" v-if="consultationWebStore.webdtlResponce.WebCngStatus !== '3'">
                    <li>
                        <button type="button" class="btnSecondary" @click="nextModal">{{ $t('consultationWebDetailArm.parts.modalButton3') }}</button>
                    </li>
                </ul>
                <ul class="btnArea center" v-if="consultationWebStore.webdtlResponce.WebCngStatus === '3' && consultationWebStore.webdtlResponce.QuestionStatus === '1'">
                    <li>
                        <button type="button" class="btnSecondary wide" @click="OpenSuerveyApply()">{{ $t('consultationWebDetailArm.parts.modalButton4') }}</button>
                    </li>
                </ul>
            </section>
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <button type="button" class="btnNormal" @click="cancel">{{ $t('common.parts.close') }}</button>
                </li>
            </ul>
        </section>
    </article>
    <article id="modalWebConsultationFinishCheck" class="modal small" :style="{ display: finishConfirmDispFlg }">
        <!--Web相談終了確認モーダル-->
        <p class="textCenter" style="margin-bottom: 20px">{{ $t('consultationWebFinishConfirm.parts.modalTitle1') }}</p>
        <p class="textCenter" style="font-size: 16px">{{ $t('consultationWebFinishConfirm.parts.modalInformation1') }}</p>
        <section class="dialogFooter">
            <ul class="btnArea center">
                <li>
                    <button type="button" class="btnNormal" @click="backModal">{{ $t('consultationWebFinishConfirm.parts.modalButton1') }}</button>
                </li>
                <li>
                    <button type="button" class="btnPrimary" @click="register('4')" :disabled="disabledStore.isDisabled">{{ $t('consultationWebFinishConfirm.parts.modalButton2') }}</button>
                </li>
            </ul>
        </section>
    </article>
    <article id="modalWebConsultationFinishComplete" class="modal small" :style="{ display: finishCompleteDispFlg }">
        <!--Web相談終了完了モーダル-->
        <p class="textCenter">{{ $t('consultationWebFinishComplete.parts.modalTitle1') }}</p>
        <section class="dialogFooter">
            <ul class="btnArea center">
                <li>
                    <button type="button" class="btnNormal" @click="cancelUpdate()">{{ $t('common.parts.close') }}</button>
                </li>
            </ul>
        </section>
    </article>
</template>
<style></style>
